import "../../styles/shared-layout.css"

import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import { Settings } from "lucide-react"
import { useEffect, useRef } from "react"
import { Outlet, useLocation, useNavigationType } from "react-router-dom"

import { IS_PROD } from "../../config/constants"
import { useIsMobile } from "../../hooks/is-mobile.ts"
import { useHasFocus } from "../../hooks/use-has-focus"
import { logger } from "../../modules/logger/logger"
import { updateBrowserTitle } from "../../modules/utils/update-browser-title"
import { SidebarComponent } from "../sidebar"
import { useSidebar } from "../use-sidebar"
import { cx, exitFullscreen, requestFullscreen } from "../utils"
import { GuideTour } from "./guide-tour.tsx"
import { SidebarContent } from "./sidebar-content.tsx"

export function SharedLayout({
  triggerFullscreen,
}: {
  triggerFullscreen: boolean
}) {
  const isMobile = useIsMobile()
  const { isVisible, setIsVisible } = useSidebar()
  const xRef = useRef<HTMLDivElement>(null)
  const hasFocus = useHasFocus()
  const prevHasFocusRef = useRef(hasFocus)
  const init = useRef(false)
  const { pathname } = useLocation()
  const navigationType = useNavigationType()
  const locationRef = useRef("")

  // Closing the sidebar also restores fullscreen on mobile (opening it exits
  // fullscreen), so the game returns to fullscreen whenever a panel closes.
  const closeSidebar = ()=> {
    setIsVisible(false)
    if (triggerFullscreen) requestFullscreen()
  }

  const onClick = ()=> {
    if (isVisible) closeSidebar()
    else {
      setIsVisible(true)
      if (triggerFullscreen) exitFullscreen()
    }
  }

  /* Route-change auto-hide. Visibility resets to false on navigation and, when
     triggered, back into fullscreen. Three independent guards keep this from
     firing spuriously:
       - `init` suppresses the very first run (first paint), so the app never
         auto-opens the sidebar / auto-requests fullscreen on load.
       - `REPLACE` navigations are redirects (the `/` → default-app hop), not
         user navigation — they must keep the sidebar's initial open state.
       - `locationRef` dedupes the same pathname, so an in-place re-render or
         re-entering the identical route is a no-op, not a re-hide. */
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler --
     route-change driven by react-router: imperative DOM side effects
     (requestFullscreen, setIsVisible reset) on pathname transition. There's
     no event handler at the navigation site to hang this off of; the init
     guard prevents an unprompted fullscreen request on first paint. */
  useEffect(()=> {
    if (!init.current) {
      init.current = true
      logger({
        input: `init.shared.layout: ${pathname}, hasFocus ${hasFocus}`,
        level: "info",
      })
      return
    }
    updateBrowserTitle()
    if (navigationType === "REPLACE") {
      // locationRef stays untouched on purpose: a later PUSH to this same
      // pathname must still close the sidebar at least once.
      logger({
        input: `redirect navigation, keeping sidebar state: ${pathname}`,
        level: "info",
      })
      return
    }
    if (locationRef.current === pathname) {
      logger({
        input: `same location, not requesting fullscreen again: ${pathname}`,
        level: "warning",
      })
      return
    }
    locationRef.current = pathname
    setIsVisible(false)
    if (triggerFullscreen) requestFullscreen()
    logger({
      input: `requesting fullscreen: ${pathname} ${hasFocus}`,
      level: "info",
    })
  }, [navigationType, pathname])
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

  // blur hides sidebar; focus return reopens it (stateful — focus-restore
  // forces isVisible=true regardless of last user toggle, so it can't be
  // derived as `localToggle && hasFocus`)
  useEffect(()=> {
    if (!IS_PROD || !isMobile) return
    const hadFocus = prevHasFocusRef.current
    prevHasFocusRef.current = hasFocus

    if (!hasFocus) {
      setIsVisible(false)
      return
    }
    if (!hadFocus) {
      setIsVisible(true)
    }
  }, [hasFocus, setIsVisible])

  return (
    <div className="layout__outer" ref={xRef}>
      <GuideTour />
      <Outlet />
      {IS_PROD && (
        <>
          <Analytics />
          <SpeedInsights />
        </>
      )}
      <SidebarComponent onClose={onClick} visible={isVisible}>
        <SidebarContent closeSidebar={closeSidebar} />
      </SidebarComponent>

      <div
        className={cx("cursor-pointer", {
          "settings-closed": !isVisible,
          "settings-open": isVisible,
        })}
        onClick={onClick}
      >
        {isVisible && isMobile ? null : <Settings />}
      </div>
    </div>
  )
}
